import React, { useEffect, useRef } from "react";
import { useStudio } from "@/studio/StudioContext";

// Spectrum analyzer reading the master bus — reacts to everything (decks, drums, synth, playlist, FX).
export default function Visualizer({ height = 90, bars = 64, variant = "bars" }) {
  const ref = useRef(null);
  const raf = useRef(null);
  const { engine } = useStudio();

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const draw = () => {
      const w = (canvas.width = canvas.offsetWidth);
      const h = (canvas.height = canvas.offsetHeight);
      ctx.clearRect(0, 0, w, h);
      const analyser = engine.masterAnalyser;
      if (!analyser) {
        raf.current = requestAnimationFrame(draw);
        return;
      }
      const bins = analyser.frequencyBinCount;
      const data = new Uint8Array(bins);

      if (variant === "scope") {
        analyser.getByteTimeDomainData(data);
        ctx.lineWidth = 2;
        ctx.strokeStyle = "#00e5ff";
        ctx.shadowBlur = 8;
        ctx.shadowColor = "#00e5ff";
        ctx.beginPath();
        const slice = w / bins;
        for (let i = 0; i < bins; i++) {
          const v = data[i] / 128;
          const y = (v * h) / 2;
          i === 0 ? ctx.moveTo(0, y) : ctx.lineTo(i * slice, y);
        }
        ctx.stroke();
        ctx.shadowBlur = 0;
      } else {
        analyser.getByteFrequencyData(data);
        const step = Math.floor(bins / bars);
        const bw = w / bars;
        for (let i = 0; i < bars; i++) {
          let sum = 0;
          for (let j = 0; j < step; j++) sum += data[i * step + j];
          const v = sum / step / 255;
          const bh = Math.max(1, v * h);
          const hue = 190 - v * 140; // cyan -> magenta as it gets loud
          const grad = ctx.createLinearGradient(0, h, 0, h - bh);
          grad.addColorStop(0, `hsla(${hue},100%,55%,0.35)`);
          grad.addColorStop(1, `hsla(${hue},100%,60%,1)`);
          ctx.fillStyle = grad;
          ctx.fillRect(i * bw + 1, h - bh, bw - 2, bh);
        }
      }
      raf.current = requestAnimationFrame(draw);
    };
    raf.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf.current);
  }, [engine, bars, variant]);

  return (
    <div
      className="w-full rounded-sm overflow-hidden border border-[#1e1e1e] bg-[#050505] relative"
      style={{ height }}
    >
      <canvas ref={ref} className="w-full h-full block" data-testid="visualizer-canvas" />
    </div>
  );
}
